import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  inject,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  OnboardingStateService,
  OnboardingPhase1Data,
  DegreeEntry,
} from '../../services/onboarding-state.service';
import {
  INDUCTION_MODULES,
  QUERETARO_UNIVERSITIES,
  CAREER_SUGGESTIONS,
  DEFAULT_PRIORITIZED_ACTIVITIES,
  QUICK_INTEREST_TAGS,
  REGULATORY_DOCUMENTS_TEXT,
  InductionModule,
} from './inductionModules';
import { CustomSelectComponent } from '../ui/custom-select/custom-select.component';
import { CustomDatepickerComponent } from '../ui/custom-datepicker/custom-datepicker.component';

interface OutlineItem {
  id: string;
  label: string;
  targetId: string;
}

export interface MissingFieldItem {
  key: string;
  label: string;
  inputId: string;
  reason: string;
}

@Component({
  selector: 'app-onboarding-phase',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    CustomSelectComponent,
    CustomDatepickerComponent,
  ],
  templateUrl: './onboarding-phase.component.html',
})
export class OnboardingPhaseComponent implements OnInit, AfterViewInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  onboardingService = inject(OnboardingStateService);

  @ViewChild('phaseForm') phaseFormRef?: ElementRef<HTMLFormElement>;

  currentPhase = 1;
  activeSectionIndex = 0;
  isSubmitting = false;
  showPassword = false;

  invalidFields: Record<string, string> = {};
  missingFieldsSummary: MissingFieldItem[] = [];
  backendErrorMsg = '';

  private autoSaveTimer?: ReturnType<typeof setTimeout>;

  // Constantes de datos
  readonly universities = QUERETARO_UNIVERSITIES;
  readonly careers = CAREER_SUGGESTIONS;
  readonly quickTags = QUICK_INTEREST_TAGS;
  readonly regulatoryTexts = REGULATORY_DOCUMENTS_TEXT;
  readonly inductionModulesList: InductionModule[] = INDUCTION_MODULES;

  readonly academicLevels = [
    { value: 'licenciatura', label: 'Licenciatura / Ingeniería' },
    { value: 'posgrado', label: 'Especialización / Maestría' },
    { value: 'doctorado', label: 'Doctorado' },
    { value: 'tecnico', label: 'Técnico Superior Universitario' },
  ];

  readonly animalExperienceOptions = [
    { value: 'actual', label: 'Tengo mascota(s) en casa actualmente' },
    { value: 'pasada', label: 'He tenido mascotas en el pasado' },
    { value: 'sin_experiencia', label: 'Sin experiencia previa con animales' },
  ];

  readonly periodTypes = [
    { value: 'semestre', label: 'Semestral' },
    { value: 'cuatrimestre', label: 'Cuatrimestral' },
    { value: 'trimestre', label: 'Trimestral' },
    { value: 'bimestre', label: 'Bimestral' },
    { value: 'tetramestre', label: 'Tetramestral' },
    { value: 'anual', label: 'Anual' },
    { value: 'modular', label: 'Modular' },
  ];

  readonly degreeLevels = [
    { value: 'preparatoria', label: 'Preparatoria / Bachillerato' },
    { value: 'tecnico', label: 'Técnico / Carrera Técnica' },
    { value: 'licenciatura', label: 'Universidad / Licenciatura / Ingeniería' },
    { value: 'especialidad', label: 'Especialidad' },
    { value: 'maestria', label: 'Maestría' },
    { value: 'doctorado', label: 'Doctorado' },
    { value: 'posdoctorado', label: 'Posdoctorado' },
  ];

  readonly degreeStatuses = [
    { value: 'completado', label: 'Graduado / Titulado / Concluido' },
    { value: 'en_curso', label: 'En curso' },
    { value: 'trunco', label: 'Pasante / En proceso de titulación' },
  ];

  // Modales
  activeDocModal: { title: string; content: string } | null = null;
  activeMediaModal: { title: string; url: string; type: string } | null = null;

  ngOnInit(): void {
    if (!this.p1.email || !this.p1.password) {
      this.router.navigate(['/auth/signup']);
      return;
    }

    if (!this.onboardingService.state.expediente.fechaInicioFormularioExpediente) {
      this.onboardingService.setExpedienteState({
        fechaInicioFormularioExpediente: new Date().toISOString(),
      });
    }

    this.route.url.subscribe((segments) => {
      const path = segments.map((s) => s.path).join('/');
      if (path.includes('phase-2')) this.currentPhase = 2;
      else if (path.includes('phase-3')) this.currentPhase = 3;
      else if (path.includes('phase-4')) this.currentPhase = 4;
      else this.currentPhase = 1;

      this.activeSectionIndex = 0;
      this.invalidFields = {};
      this.missingFieldsSummary = [];
      this.backendErrorMsg = '';

      if (!this.p1.socialMedia) {
        this.p1.socialMedia = {
          facebook: '',
          instagram: '',
          tiktok: '',
          x: '',
          youtube: '',
          linkedin: '',
        };
      }

      if (this.p1.userType === 'SS') {
        if (!this.p1.periodNumber) {
          const digits = (this.p1.semester || '').match(/\d+/);
          this.p1.periodNumber = digits ? digits[0] : 1;
        }
        if (!this.p1.periodType) {
          this.p1.periodType = 'semestre';
        }
        this.updateComputedSemester();
      }

      this.focusFirstControl();
    });
  }

  ngAfterViewInit(): void {
    this.focusFirstControl();
  }

  get p1(): OnboardingPhase1Data {
    return this.onboardingService.state.phase1;
  }

  get p2() {
    return this.onboardingService.state.phase2;
  }

  get p3() {
    return this.onboardingService.state.phase3;
  }

  get p4() {
    return this.onboardingService.state.phase4;
  }

  get outlineItems(): OutlineItem[] {
    if (this.currentPhase === 1) {
      return [
        { id: 'out-p1-1', label: '01 · Creación de cuenta', targetId: 'sec-account' },
        { id: 'out-p1-2', label: '02 · Datos personales', targetId: 'sec-personal' },
        {
          id: 'out-p1-3',
          label:
            this.p1.userType === 'SS'
              ? '03 · Datos académicos'
              : '03 · Formación y Nivel de Estudios',
          targetId: 'sec-academic',
        },
        { id: 'out-p1-4', label: '04 · Redes sociales', targetId: 'sec-socials' },
      ];
    }
    if (this.currentPhase === 2) {
      return this.inductionModulesList.map((m, idx) => ({
        id: `out-p2-${m.id}`,
        label: `0${idx + 1} · ${m.title.replace(/^\d+\.\s*/, '')}`,
        targetId: m.targetId,
      }));
    }
    if (this.currentPhase === 3) {
      return [
        { id: 'out-p3-1', label: '01 · Semblanza y Biografía', targetId: 'sec-about-bio' },
        { id: 'out-p3-2', label: '02 · Talentos y Experiencia', targetId: 'sec-about-talents' },
        { id: 'out-p3-3', label: '03 · Motivación Principal', targetId: 'sec-about-motivation' },
        { id: 'out-p3-4', label: '04 · Prioridad de Actividades', targetId: 'sec-about-priorities' },
      ];
    }
    return [
      { id: 'out-p4-1', label: '01 · Enlaces en la nube (Drive/OneDrive)', targetId: 'sec-docs-links' },
      { id: 'out-p4-2', label: '02 · Declaraciones y Reglamento', targetId: 'sec-docs-consents' },
      { id: 'out-p4-3', label: '03 · Confirmación y Envío', targetId: 'sec-docs-confirm' },
    ];
  }

  getPhaseSubsections(phaseNum: number): { id: string; label: string; targetId: string }[] {
    if (phaseNum === 1) {
      return [
        { id: 'p1-sub-1', label: 'Cuenta', targetId: 'sec-account' },
        { id: 'p1-sub-2', label: 'Personales', targetId: 'sec-personal' },
        { id: 'p1-sub-3', label: 'Académicos', targetId: 'sec-academic' },
        { id: 'p1-sub-4', label: 'Redes', targetId: 'sec-socials' },
      ];
    }
    if (phaseNum === 2) {
      return this.inductionModulesList.map((m, idx) => ({
        id: `p2-sub-${m.id}`,
        label: `Módulo ${idx + 1}`,
        targetId: m.targetId,
      }));
    }
    if (phaseNum === 3) {
      return [
        { id: 'p3-sub-1', label: 'Semblanza', targetId: 'sec-about-bio' },
        { id: 'p3-sub-2', label: 'Talentos', targetId: 'sec-about-talents' },
        { id: 'p3-sub-3', label: 'Motivación', targetId: 'sec-about-motivation' },
        { id: 'p3-sub-4', label: 'Actividades', targetId: 'sec-about-priorities' },
      ];
    }
    return [
      { id: 'p4-sub-1', label: 'Documentos', targetId: 'sec-docs-links' },
      { id: 'p4-sub-2', label: 'Reglamento', targetId: 'sec-docs-consents' },
      { id: 'p4-sub-3', label: 'Confirmación', targetId: 'sec-docs-confirm' },
    ];
  }

  getSubsectionProgress(phaseNum: number, subIndex: number): number {
    if (this.isPhaseDone(phaseNum)) return 100;

    if (phaseNum === 1) {
      if (subIndex === 0) {
        let filled = 0;
        if (this.p1.email?.trim()) filled++;
        if (this.p1.password?.trim()) filled++;
        return Math.round((filled / 2) * 100);
      }
      if (subIndex === 1) {
        let filled = 0;
        if (this.p1.names?.trim()) filled++;
        if (this.p1.apellidoPaterno?.trim()) filled++;
        if (this.p1.apellidoMaterno?.trim()) filled++;
        if (this.p1.birthDate?.trim()) filled++;
        if (this.p1.phone?.trim()) filled++;
        return Math.round((filled / 5) * 100);
      }
      if (subIndex === 2) {
        if (this.p1.userType === 'SS') {
          let filled = 0;
          if (this.p1.curp?.trim()) filled++;
          if (this.p1.studentId?.trim()) filled++;
          if (this.p1.academicLevel?.trim()) filled++;
          if (this.p1.university?.trim()) filled++;
          if (this.p1.carrera?.trim()) filled++;
          if (this.p1.periodType?.trim()) filled++;
          if (this.p1.semester?.trim()) filled++;
          return Math.round((filled / 7) * 100);
        } else {
          let filled = 0;
          if (this.p1.occupation?.trim()) filled++;
          if (this.p1.degrees && this.p1.degrees.length > 0) filled++;
          return Math.round((filled / 2) * 100);
        }
      }
      if (subIndex === 3) {
        return 100;
      }
    }

    if (phaseNum === 2) {
      const modId = subIndex + 1;
      const ref = this.p2.reflections?.[`modulo-${modId}`];
      const isChecked = (this.p2.completedModules || []).includes(modId);
      let filled = 0;
      const total = modId === 1 ? 5 : 4;
      if (modId === 1 && this.p2.socialsFollowed) filled++;
      if (ref?.liked?.trim()) filled++;
      if (ref?.important?.trim()) filled++;
      if (ref?.improve?.trim()) filled++;
      if (isChecked) filled++;
      return Math.round((filled / total) * 100);
    }

    if (phaseNum === 3) {
      if (subIndex === 0) {
        let filled = 0;
        if (this.p3.bioPersonal?.trim()) filled++;
        if (this.p3.intereses?.trim()) filled++;
        return Math.round((filled / 2) * 100);
      }
      if (subIndex === 1) {
        let filled = 0;
        if (this.p3.talentos?.trim()) filled++;
        if (this.p3.animalExperience?.trim()) filled++;
        if (this.p3.aportesAbaq?.trim()) filled++;
        return Math.round((filled / 3) * 100);
      }
      if (subIndex === 2) {
        let filled = 0;
        if (this.p3.descExperiencia?.trim()) filled++;
        if (this.p3.motivacionAbaq?.trim()) filled++;
        return Math.round((filled / 2) * 100);
      }
      if (subIndex === 3) {
        return (this.p3.rankedActivities || []).length > 0 ? 100 : 0;
      }
    }

    if (phaseNum === 4) {
      if (subIndex === 0) {
        let filled = 0;
        if (this.p4.cloudUrls?.ine?.trim()) filled++;
        if (this.p4.cloudUrls?.cv?.trim()) filled++;
        if (this.p4.cloudUrls?.comprobante?.trim()) filled++;
        return Math.round((filled / 3) * 100);
      }
      if (subIndex === 1) {
        let filled = 0;
        if (this.p4.consents?.reglamento) filled++;
        if (this.p4.consents?.privacidad) filled++;
        if (this.p4.consents?.etica) filled++;
        if (this.p4.consents?.incognito) filled++;
        if (this.p4.consents?.consentimiento) filled++;
        return Math.round((filled / 5) * 100);
      }
      if (subIndex === 2) {
        const linksDone = this.getSubsectionProgress(4, 0) === 100;
        const consentsDone = this.getSubsectionProgress(4, 1) === 100;
        return linksDone && consentsDone ? 100 : 0;
      }
    }

    return 0;
  }

  getPhaseProgress(phaseNum: number): number {
    if (this.isPhaseDone(phaseNum)) return 100;
    const subs = this.getPhaseSubsections(phaseNum);
    if (!subs.length) return 0;
    let total = 0;
    for (let i = 0; i < subs.length; i++) {
      total += this.getSubsectionProgress(phaseNum, i);
    }
    return Math.round(total / subs.length);
  }

  isPhaseDone(phaseNum: number): boolean {
    return (
      this.currentPhase > phaseNum ||
      (this.onboardingService.state.completedPhases || []).includes(phaseNum)
    );
  }

  getPhaseStatusText(phaseNum: number): string {
    if (this.isPhaseDone(phaseNum)) {
      return 'Completa';
    }
    if (this.currentPhase === phaseNum) {
      if (phaseNum === 2) {
        const doneMods = (this.p2.completedModules || []).length;
        return `${doneMods}/6 módulos`;
      }
      const subs = this.getPhaseSubsections(phaseNum);
      let completedCount = 0;
      for (let i = 0; i < subs.length; i++) {
        if (this.getSubsectionProgress(phaseNum, i) === 100) completedCount++;
      }
      return `${completedCount}/${subs.length} subsecciones`;
    }
    return 'Pendiente';
  }

  isSectionCompleted(targetId: string, idx: number): boolean {
    return this.getSubsectionProgress(this.currentPhase, idx) === 100;
  }

  getQuestionPlaceholder(modId: number, key: 'liked' | 'important' | 'improve'): string {
    const mod = this.inductionModulesList.find((m) => m.id === modId);
    const q = mod?.questions?.find((qu) => qu.key === key);
    return q?.placeholder || '';
  }

  @HostListener('focusin', ['$event'])
  onFocusIn(event: FocusEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const items = this.outlineItems;
    for (let i = 0; i < items.length; i++) {
      const secEl = document.getElementById(items[i].targetId);
      if (secEl && (secEl === target || secEl.contains(target))) {
        this.activeSectionIndex = i;
        break;
      }
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    const items = this.outlineItems;
    if (!items.length) return;
    const midY = window.innerHeight * 0.45;
    let closestIdx = this.activeSectionIndex;
    let minDistance = Infinity;

    for (let i = 0; i < items.length; i++) {
      const el = document.getElementById(items[i].targetId);
      if (el) {
        const rect = el.getBoundingClientRect();
        const elementMid = (rect.top + rect.bottom) / 2;
        const dist = Math.abs(elementMid - midY);
        if (rect.top <= midY + 200 && rect.bottom >= midY - 200 && dist < minDistance) {
          minDistance = dist;
          closestIdx = i;
        }
      }
    }
    this.activeSectionIndex = closestIdx;
  }

  // Estado de guardado en vivo por sección de campos (Semáforo: 'saved' | 'saving' | 'error')
  sectionSaveStatus: Record<string, 'saved' | 'saving' | 'error'> = {
    account: 'saved',
    personal: 'saved',
    academic: 'saved',
    social: 'saved',
    induction: 'saved',
    survey_bio: 'saved',
    survey_priorities: 'saved',
    docs_links: 'saved',
    docs_consents: 'saved',
  };
  private sectionDebounceTimers: Record<string, any> = {};
  private sectionMinDisplayTimers: Record<string, any> = {};

  getSectionStatus(secId: string): 'saved' | 'saving' | 'error' {
    return this.sectionSaveStatus[secId] || 'saved';
  }

  getSectionIcon(secId: string): string {
    const status = this.getSectionStatus(secId);
    if (status === 'saving') return 'sync';
    if (status === 'error') return 'cloud_off';
    return 'cloud_done';
  }

  getSectionLabel(secId: string): string {
    const status = this.getSectionStatus(secId);
    if (status === 'saving') return 'Guardando...';
    if (status === 'error') return 'Error al guardar';
    return 'Guardado';
  }

  getSectionStatusTitle(secId: string): string {
    const status = this.getSectionStatus(secId);
    if (status === 'saving') return 'Guardando cambios automáticamente en tu navegador...';
    if (status === 'error') return 'Error al guardar los cambios';
    return 'Progreso guardado automáticamente en tu navegador';
  }

  resolveSectionByField(fieldKey?: string): string {
    if (!fieldKey) {
      if (this.currentPhase === 1) return 'account';
      if (this.currentPhase === 2) return 'induction';
      if (this.currentPhase === 3) return 'survey_bio';
      if (this.currentPhase === 4) return 'docs_links';
      return 'account';
    }

    if (['email', 'password', 'confirmPassword'].includes(fieldKey)) {
      return 'account';
    }
    if ([
      'names', 'apellidoPaterno', 'apellidoMaterno', 'birthDate', 'phone',
      'hasWhatsapp', 'whatsappNumber', 'hasSecondaryContact', 'secondaryPhone',
      'curp', 'studentId'
    ].includes(fieldKey)) {
      return 'personal';
    }
    if ([
      'academicLevel', 'university', 'carrera', 'periodType', 'periodNumber',
      'semester', 'serviceStartDate', 'serviceEndDate', 'occupation', 'degrees', 'degree'
    ].includes(fieldKey) || fieldKey.startsWith('degreeInst_') || fieldKey.startsWith('degreeTitle_')) {
      return 'academic';
    }
    if (['socialMedia', 'facebook', 'instagram', 'tiktok', 'x', 'youtube', 'linkedin'].includes(fieldKey)) {
      return 'social';
    }
    if (fieldKey === 'socialsFollowed' || fieldKey.startsWith('reflections_') || fieldKey === 'modules') {
      return 'induction';
    }
    if ([
      'bioPersonal', 'intereses', 'talentos', 'animalExperience',
      'aportesAbaq', 'descExperiencia', 'motivacionAbaq', 'interestTag'
    ].includes(fieldKey)) {
      return 'survey_bio';
    }
    if (['rankedActivities', 'priorities'].includes(fieldKey)) {
      return 'survey_priorities';
    }
    if (['cloudIne', 'cloudCv', 'cloudComp'].includes(fieldKey)) {
      return 'docs_links';
    }
    if ([
      'consentReglamento', 'consentPrivacidad', 'consentEtica',
      'consentIncognito', 'consentFirma', 'consent'
    ].includes(fieldKey)) {
      return 'docs_consents';
    }

    return 'account';
  }

  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    this.persistCurrentDraft();
  }

  persistCurrentDraft(): void {
    if (this.currentPhase === 1) this.onboardingService.updatePhase1({ ...this.p1 });
    else if (this.currentPhase === 2) this.onboardingService.updatePhase2({ ...this.p2 });
    else if (this.currentPhase === 3) this.onboardingService.updatePhase3({ ...this.p3 });
    else if (this.currentPhase === 4) this.onboardingService.updatePhase4({ ...this.p4 });
  }

  onFieldChange(fieldKey?: string, sectionId?: string): void {
    if (fieldKey) {
      this.clearFieldError(fieldKey);
    }
    const resolvedSection = sectionId || this.resolveSectionByField(fieldKey);
    this.markSectionSaving(resolvedSection);
  }

  markSectionSaving(sectionId: string): void {
    // 1. Mostrar de inmediato estado Guardando (amarillo / ámbar con animación de giro)
    this.sectionSaveStatus[sectionId] = 'saving';

    if (this.sectionMinDisplayTimers[sectionId]) {
      clearTimeout(this.sectionMinDisplayTimers[sectionId]);
      delete this.sectionMinDisplayTimers[sectionId];
    }

    if (this.sectionDebounceTimers[sectionId]) {
      clearTimeout(this.sectionDebounceTimers[sectionId]);
    }

    // 2. Debounce para persistir datos reales en memoria y localStorage
    this.sectionDebounceTimers[sectionId] = setTimeout(() => {
      try {
        this.persistCurrentDraft();

        // 3. Mantener "Guardando..." al menos 500ms para feedback visual perceptible
        this.sectionMinDisplayTimers[sectionId] = setTimeout(() => {
          this.sectionSaveStatus[sectionId] = 'saved';
          delete this.sectionMinDisplayTimers[sectionId];
        }, 500);
      } catch {
        this.sectionSaveStatus[sectionId] = 'error';
      } finally {
        delete this.sectionDebounceTimers[sectionId];
      }
    }, 400);
  }

  ngOnDestroy(): void {
    Object.keys(this.sectionDebounceTimers).forEach((sec) => {
      if (this.sectionDebounceTimers[sec]) clearTimeout(this.sectionDebounceTimers[sec]);
    });
    Object.keys(this.sectionMinDisplayTimers).forEach((sec) => {
      if (this.sectionMinDisplayTimers[sec]) clearTimeout(this.sectionMinDisplayTimers[sec]);
    });
    this.persistCurrentDraft();
  }

  formatPhoneDigits(raw: string): string {
    const digits = (raw || '').replace(/\D/g, '').slice(0, 10);
    if (digits.length > 6) {
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    } else if (digits.length > 3) {
      return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    }
    return digits;
  }

  onPhoneChange(field: 'phone' | 'whatsappNumber' | 'secondaryPhone', val: string): void {
    const formatted = this.formatPhoneDigits(val);
    if (field === 'phone') this.p1.phone = formatted;
    else if (field === 'whatsappNumber') this.p1.whatsappNumber = formatted;
    else if (field === 'secondaryPhone') this.p1.secondaryPhone = formatted;
    this.onFieldChange(field, 'personal');
  }

  clearFieldError(key: string): void {
    if (this.invalidFields[key]) {
      const next = { ...this.invalidFields };
      delete next[key];
      this.invalidFields = next;
      this.missingFieldsSummary = this.missingFieldsSummary.filter((m) => m.key !== key);
    }
    this.backendErrorMsg = '';
  }

  // --- MÓDULOS DE INDUCCIÓN (FASE 2) ---
  isModuleCompleted(modId: number): boolean {
    return (this.p2.completedModules || []).includes(modId);
  }

  toggleModule(modId: number): void {
    const current = new Set(this.p2.completedModules || []);
    if (current.has(modId)) {
      current.delete(modId);
    } else {
      current.add(modId);
    }
    this.p2.completedModules = Array.from(current);
    this.onboardingService.updatePhase2({ completedModules: this.p2.completedModules });
    this.clearFieldError('modules');
    this.onFieldChange('modules', 'induction');
  }

  openMediaModal(title: string, url: string, type = 'youtube'): void {
    this.activeMediaModal = { title, url, type };
  }

  closeMediaModal(): void {
    this.activeMediaModal = null;
  }

  // --- SOBRE MÍ (FASE 3) ---
  addInterestTag(tag: string): void {
    const current = (this.p3.intereses || '').trim();
    if (!current) {
      this.p3.intereses = tag;
    } else {
      const tagsList = current.split(',').map((t) => t.trim().toLowerCase());
      if (!tagsList.includes(tag.toLowerCase())) {
        this.p3.intereses = `${current}, ${tag}`;
      }
    }
    this.onFieldChange('intereses', 'survey_bio');
  }

  moveActivityUp(index: number): void {
    if (index <= 0) return;
    const arr = [...this.p3.rankedActivities];
    const temp = arr[index - 1];
    arr[index - 1] = arr[index];
    arr[index] = temp;
    this.p3.rankedActivities = arr;
    this.onFieldChange('priorities', 'survey_priorities');
  }

  moveActivityDown(index: number): void {
    if (index >= this.p3.rankedActivities.length - 1) return;
    const arr = [...this.p3.rankedActivities];
    const temp = arr[index + 1];
    arr[index + 1] = arr[index];
    arr[index] = temp;
    this.p3.rankedActivities = arr;
    this.onFieldChange('priorities', 'survey_priorities');
  }

  getActivityDisplayText(act: string): string {
    if (this.p1.userType === 'SS') {
      return act;
    }
    // Para voluntario general, las horas de servicio social no aplican; mostrar solo la actividad
    return act.replace(/\s*\((Acredita|Horas)[\s\S]*?\)/gi, '').trim();
  }

  // --- DOCUMENTOS Y CONSENTIMIENTOS (FASE 4) ---
  openDocModal(docKey: 'reglamento' | 'privacidad' | 'etica' | 'terminos'): void {
    const doc = this.regulatoryTexts[docKey];
    if (doc) {
      this.activeDocModal = { title: doc.title, content: doc.content };
    }
  }

  closeDocModal(): void {
    this.activeDocModal = null;
  }

  // --- FORMACIÓN MODULAR (VOLUNTARIO GENERAL - FASE 1) ---
  addDegree(): void {
    const degrees: DegreeEntry[] = [
      ...(this.p1.degrees || []),
      {
        level: 'maestria',
        institution: '',
        title: '',
        status: 'completado',
      },
    ];
    this.p1.degrees = degrees;
    this.onboardingService.updatePhase1({ degrees });
    this.onFieldChange('degrees', 'academic');
  }

  removeDegree(index: number): void {
    if ((this.p1.degrees || []).length <= 1) return;
    const updated = this.p1.degrees.filter((_, idx) => idx !== index);
    this.p1.degrees = updated;
    this.onboardingService.updatePhase1({ degrees: updated });
    this.onFieldChange('degrees', 'academic');
  }

  // --- NAVEGACIÓN Y TECLADO ---
  focusField(inputId: string): void {
    const el = document.getElementById(inputId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        (el as HTMLElement).focus();
      }, 120);
    }
  }

  focusFirstControl(): void {
    setTimeout(() => {
      const formEl = this.phaseFormRef?.nativeElement;
      if (!formEl) return;
      const firstInput = formEl.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
      );
      firstInput?.focus();
    }, 80);
  }

  handleFormKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const tag = (event.target as HTMLElement)?.tagName?.toLowerCase();
    if (tag === 'select' && event.altKey) return;
    const formEl = this.phaseFormRef?.nativeElement;
    if (!formEl) return;
    const focusables = Array.from(
      formEl.querySelectorAll<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button[type="submit"]:not([disabled])',
      ),
    ).filter((el) => el.offsetParent !== null);

    const idx = focusables.indexOf(event.target as HTMLElement);
    if (idx === -1) return;
    event.preventDefault();
    const nextIdx =
      event.key === 'ArrowDown'
        ? (idx + 1) % focusables.length
        : (idx - 1 + focusables.length) % focusables.length;
    const nextEl = focusables[nextIdx];
    if (nextEl) {
      nextEl.focus();
      nextEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  scrollToSection(targetId: string, idx: number): void {
    this.activeSectionIndex = idx;
    const el = document.getElementById(targetId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  handleOutlineKeyDown(event: KeyboardEvent, targetId: string, idx: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.scrollToSection(targetId, idx);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      const items = this.outlineItems;
      const next = (idx + 1) % items.length;
      this.scrollToSection(items[next].targetId, next);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const items = this.outlineItems;
      const prev = (idx - 1 + items.length) % items.length;
      this.scrollToSection(items[prev].targetId, prev);
    }
  }

  switchUserType(type: 'SS' | 'VG'): void {
    this.invalidFields = {};
    this.missingFieldsSummary = [];
    this.onboardingService.updatePhase1({ userType: type });
    if (type === 'SS') {
      if (!this.p1.periodNumber) {
        this.p1.periodNumber = 1;
      }
      if (!this.p1.periodType) {
        this.p1.periodType = 'semestre';
      }
      this.updateComputedSemester();
    }
    this.focusFirstControl();
  }

  getOrdinalSuffix(val: number | string | undefined | null): string {
    const n = Number(val);
    if (!n || isNaN(n)) return '°';
    if (n === 1 || n === 3) return 'er';
    if (n === 2) return 'do';
    if (n >= 4 && n <= 6) return 'to';
    if (n === 7 || n === 10) return 'mo';
    if (n === 8) return 'vo';
    if (n === 9) return 'no';
    if (n === 11 || n === 12) return 'vo';
    return '°';
  }

  getPeriodSingular(type: string | undefined): string {
    switch (type?.toLowerCase()) {
      case 'cuatrimestre':
      case 'cuatrimestral':
        return 'Cuatrimestre';
      case 'trimestre':
      case 'trimestral':
        return 'Trimestre';
      case 'bimestre':
      case 'bimestral':
        return 'Bimestre';
      case 'tetramestre':
      case 'tetramestral':
        return 'Tetramestre';
      case 'anual':
      case 'ano':
      case 'año':
        return 'Año';
      case 'modular':
      case 'modulo':
      case 'módulo':
        return 'Módulo';
      case 'semestre':
      case 'semestral':
      default:
        return 'Semestre';
    }
  }

  getLockedPeriodLabel(): string {
    const typeLabel = this.getPeriodSingular(this.p1.periodType);
    const num = this.p1.periodNumber;
    if (!num || !String(num).trim()) {
      return `° ${typeLabel}`;
    }
    const suffix = this.getOrdinalSuffix(num);
    return `${suffix} ${typeLabel}`;
  }

  onPeriodNumberChange(val: string | number): void {
    const raw = String(val ?? '').replace(/\D/g, '');
    this.p1.periodNumber = raw;
    this.updateComputedSemester();
    this.onFieldChange('periodNumber', 'academic');
  }

  onPeriodTypeChange(newType: string): void {
    this.p1.periodType = newType;
    this.updateComputedSemester();
    this.onFieldChange('periodType', 'academic');
  }

  updateComputedSemester(): void {
    const num = this.p1.periodNumber;
    const typeLabel = this.getPeriodSingular(this.p1.periodType);
    if (num && String(num).trim()) {
      const suffix = this.getOrdinalSuffix(num);
      this.p1.semester = `${num}${suffix} ${typeLabel}`;
    } else {
      this.p1.semester = '';
    }
    this.clearFieldError('semester');
  }

  saveDraft(): void {
    this.onboardingService.updatePhase1({ ...this.p1 });
    this.onboardingService.updatePhase2({ ...this.p2 });
    this.onboardingService.updatePhase3({ ...this.p3 });
    this.onboardingService.updatePhase4({ ...this.p4 });
  }

  // --- VALIDACIÓN RIGUROSA ---
  validatePhase(phase: number): boolean {
    const errors: Record<string, string> = {};
    const summary: MissingFieldItem[] = [];

    const addErr = (key: string, label: string, inputId: string, reason: string) => {
      errors[key] = reason;
      summary.push({ key, label, inputId, reason });
    };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const urlRegex = /^https?:\/\/.+/i;

    if (phase === 1) {
      const email = (this.p1.email || '').trim();
      if (!email) {
        addErr('email', 'Correo electrónico', 'p1-email', 'El correo electrónico es obligatorio');
      } else if (!emailRegex.test(email)) {
        addErr('email', 'Correo electrónico', 'p1-email', 'Ingresa un correo electrónico con formato válido');
      }

      const pwd = this.p1.password || '';
      if (!pwd) {
        addErr('password', 'Contraseña', 'p1-password', 'La contraseña es obligatoria (mínimo 8 caracteres)');
      } else if (pwd.length < 8) {
        addErr('password', 'Contraseña', 'p1-password', `Debe tener al menos 8 caracteres (actualmente tiene ${pwd.length})`);
      }

      if (!(this.p1.names || '').trim()) {
        addErr('names', 'Nombre(s)', 'p1-names', 'Ingresa tu(s) nombre(s)');
      }

      if (!(this.p1.apellidoPaterno || '').trim()) {
        addErr('apellidoPaterno', 'Apellido Paterno', 'p1-apellidoPaterno', 'Ingresa tu apellido paterno');
      }

      if (!(this.p1.birthDate || '').trim()) {
        addErr('birthDate', 'Fecha de nacimiento', 'p1-birthdate', 'Selecciona tu fecha de nacimiento');
      }

      const cleanPhone = (this.p1.phone || '').replace(/\D/g, '');
      if (!cleanPhone) {
        addErr('phone', 'Teléfono celular principal', 'p1-phone', 'El teléfono celular es obligatorio');
      } else if (cleanPhone.length < 10) {
        addErr('phone', 'Teléfono celular principal', 'p1-phone', `Debe tener 10 dígitos numéricos (tiene ${cleanPhone.length})`);
      }

      if (!this.p1.hasWhatsapp) {
        const cleanW = (this.p1.whatsappNumber || '').replace(/\D/g, '');
        if (!cleanW) {
          addErr('whatsappNumber', 'Número con WhatsApp', 'p1-whatsapp', 'Ingresa tu número con WhatsApp activo');
        } else if (cleanW.length < 10) {
          addErr('whatsappNumber', 'Número con WhatsApp', 'p1-whatsapp', 'El número de WhatsApp debe tener 10 dígitos');
        }
      }

      if (this.p1.hasSecondaryContact) {
        const cleanSec = (this.p1.secondaryPhone || '').replace(/\D/g, '');
        if (!cleanSec) {
          addErr('secondaryPhone', 'Segundo número de contacto', 'p1-secondaryPhone', 'Ingresa el número secundario');
        } else if (cleanSec.length < 10) {
          addErr('secondaryPhone', 'Segundo número de contacto', 'p1-secondaryPhone', 'El número de contacto secundario debe tener 10 dígitos');
        }
      }

      if (this.p1.userType === 'SS') {
        const curpClean = (this.p1.curp || '').trim().toUpperCase();
        if (!curpClean) {
          addErr('curp', 'CURP', 'p1-curp', 'La CURP es obligatoria para Servicio Social');
        } else if (curpClean.length < 18) {
          addErr('curp', 'CURP', 'p1-curp', `La CURP debe tener 18 caracteres (tiene ${curpClean.length})`);
        }

        if (!(this.p1.studentId || '').trim()) {
          addErr('studentId', 'Matrícula o ID Estudiantil', 'p1-studentId', 'Ingresa tu matrícula o ID universitario');
        }

        if (!(this.p1.university || '').trim()) {
          addErr('university', 'Institución académica', 'p1-university', 'Ingresa tu universidad o institución');
        }

        if (!(this.p1.carrera || '').trim()) {
          addErr('carrera', 'Carrera', 'p1-carrera', 'Ingresa tu carrera');
        }

        if (!(this.p1.semester || '').trim()) {
          addErr('semester', 'Esquema de periodos y avance', 'p1-period-number', 'Ingresa tu número de avance en el periodo (ej. 4)');
        }
      } else {
        const degrees = this.p1.degrees || [];
        degrees.forEach((deg, idx) => {
          if (!(deg.institution || '').trim()) {
            addErr(`degreeInst_${idx}`, `Institución (Estudio #${idx + 1})`, `deg-inst-${idx}`, `Ingresa la institución del estudio #${idx + 1}`);
          }
          if (!(deg.title || '').trim()) {
            addErr(`degreeTitle_${idx}`, `Carrera / Programa (Estudio #${idx + 1})`, `deg-title-${idx}`, `Ingresa la carrera o programa del estudio #${idx + 1}`);
          }
        });
      }
    } else if (phase === 2) {
      if (!this.p2.socialsFollowed) {
        addErr(
          'socialsFollowed',
          'Seguimiento de redes de ABAQ',
          'p2-socials-check',
          'Debes confirmar haber visitado la web y seguido las redes oficiales',
        );
      }

      const completed = this.p2.completedModules || [];
      if (completed.length < this.inductionModulesList.length) {
        addErr(
          'modules',
          'Módulos certificados (6 de 6)',
          'conociendo-abaq',
          'Debes certificar la comprensión y revisión de los 6 módulos',
        );
      }

      for (const mod of this.inductionModulesList) {
        const ref = this.p2.reflections?.[`modulo-${mod.id}`];
        if (!ref?.liked?.trim() || !ref?.important?.trim() || !ref?.improve?.trim()) {
          addErr(
            `reflections_${mod.id}`,
            `Reflexiones del Módulo ${mod.number}`,
            mod.targetId,
            `Debes completar las 3 reflexiones para ${mod.title}`,
          );
        }
      }
    } else if (phase === 3) {
      if (!(this.p3.bioPersonal || '').trim()) {
        addErr('bioPersonal', 'Semblanza personal / Bio', 'p3-bio', 'Escribe tu semblanza o bio personal');
      }
      if (!(this.p3.intereses || '').trim()) {
        addErr('intereses', 'Áreas de interés', 'p3-intereses', 'Indica al menos un área de interés');
      }
      if (!(this.p3.talentos || '').trim()) {
        addErr('talentos', 'Principales talentos y habilidades', 'p3-talentos', 'Describe tus talentos principales');
      }
      if (!(this.p3.aportesAbaq || '').trim()) {
        addErr('aportesAbaq', 'Talentos y conocimientos para compartir', 'p3-aportes', 'Comparte cómo puedes sumar a ABAQ');
      }
      if (!(this.p3.motivacionAbaq || '').trim()) {
        addErr('motivacionAbaq', 'Motivación institucional', 'p3-motivacion', 'Describe tu motivación para sumarte');
      }
    } else if (phase === 4) {
      const ine = (this.p4.cloudUrls.ine || '').trim();
      if (!ine) {
        addErr('cloudIne', 'Identificación oficial (Liga URL)', 'doc-ine', 'Ingresa la liga compartida de tu identificación');
      } else if (!urlRegex.test(ine)) {
        addErr('cloudIne', 'Identificación oficial (Liga URL)', 'doc-ine', 'Debe ser un enlace que inicie con https:// o http://');
      }

      const cv = (this.p4.cloudUrls.cv || '').trim();
      if (!cv) {
        addErr('cloudCv', 'Currículum Vitae (Liga URL)', 'doc-cv', 'Ingresa la liga compartida de tu CV o ficha');
      } else if (!urlRegex.test(cv)) {
        addErr('cloudCv', 'Currículum Vitae (Liga URL)', 'doc-cv', 'Debe ser un enlace que inicie con https:// o http://');
      }

      const comp = (this.p4.cloudUrls.comprobante || '').trim();
      if (!comp) {
        addErr('cloudComp', 'Constancia o comprobante (Liga URL)', 'doc-comp', 'Ingresa la liga compartida de tu constancia o comprobante');
      } else if (!urlRegex.test(comp)) {
        addErr('cloudComp', 'Constancia o comprobante (Liga URL)', 'doc-comp', 'Debe ser un enlace que inicie con https:// o http://');
      }

      if (!this.p4.consents.reglamento) {
        addErr('consentReglamento', 'Reglamento de Servicio Social', 'consent-reglamento', 'Debes aceptar el reglamento');
      }
      if (!this.p4.consents.privacidad) {
        addErr('consentPrivacidad', 'Aviso de Privacidad', 'consent-privacidad', 'Debes aceptar el aviso de privacidad');
      }
      if (!this.p4.consents.etica) {
        addErr('consentEtica', 'Código de Ética Institucional', 'consent-etica', 'Debes aceptar el código de ética');
      }
      if (!this.p4.consents.incognito) {
        addErr('consentIncognito', 'Verificación de enlaces en incógnito', 'consent-incognito', 'Debes confirmar la verificación de enlaces');
      }
      if (!this.p4.consents.consentimiento) {
        addErr('consentFirma', 'Firma y consentimiento tácito', 'consent-firma', 'Debes aceptar los términos y condiciones');
      }
    }

    this.invalidFields = errors;
    this.missingFieldsSummary = summary;

    if (summary.length > 0) {
      setTimeout(() => {
        this.focusField(summary[0].inputId);
      }, 60);
      return false;
    }

    return true;
  }

  goToPhase(targetPhase: number): void {
    this.saveDraft();
    if (targetPhase > this.currentPhase) {
      if (!this.validatePhase(this.currentPhase)) {
        return;
      }
      this.onboardingService.markPhaseCompleted(this.currentPhase);
    }
    this.router.navigate([`/onboarding/phase-${targetPhase}`]);
  }

  async onSubmitPhase(): Promise<void> {
    this.backendErrorMsg = '';
    this.saveDraft();

    if (!this.validatePhase(this.currentPhase)) {
      return;
    }

    this.onboardingService.markPhaseCompleted(this.currentPhase);

    if (this.currentPhase < 4) {
      this.router.navigate([`/onboarding/phase-${this.currentPhase + 1}`]);
      return;
    }

    // Validar Fases 1 a 3 previas antes de enviar a MongoDB
    for (const p of [1, 2, 3]) {
      if (!this.validatePhase(p)) {
        this.router.navigate([`/onboarding/phase-${p}`]);
        return;
      }
    }

    this.isSubmitting = true;
    const result = await this.onboardingService.submitDossierToBackend();
    this.isSubmitting = false;

    if (!result.ok) {
      this.backendErrorMsg =
        result.error || 'No se pudo crear el usuario en la base de datos.';
      // Mostrar al usuario el error al inicio desplazando la pantalla inmediatamente
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const alertEl = document.querySelector('.validation-alert-banner') || document.querySelector('.onboarding-card');
        alertEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 60);
      return;
    }

    this.router.navigate(['/onboarding/expediente']);
  }
}
